// frontend/src/pages/merchant/dashboard/CataloguePage.jsx
import React, { useEffect, useState } from "react";
import {
  Camera, User, MapPin, Plus, Trash2, Edit3, Save, Upload, Image, Package, FolderOpen, CheckCircle, AlertCircle, X, Layers,} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDashboard,
  createCatalogueItem,
  updateCatalogueItem,
  deleteCatalogueItem,
} from "../../../features/merchant/dashboardThunks";
import { toast } from "react-toastify";
import "./dashboard.css";

const CataloguePage = () => {
  const dispatch = useDispatch();

  const { data: dashboardData, loading: dashboardLoading, updating } =
    useSelector((state) => state.dashboard || {});

  const [isEditing, setIsEditing] = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [catalogueItems, setCatalogueItems] = useState([
    {
      id: 1,
      name: "",
      location: "",
      imageUrl: null,
      file: null,
      preview: null,
      _id: null,
    },
  ]);
  const [deletedIds, setDeletedIds] = useState([]);

  useEffect(() => {
    dispatch(fetchDashboard());
  }, [dispatch]);

  useEffect(() => {
    if (!dashboardData) return;

    setDocumentId(dashboardData.documentId || dashboardData._id || null);

    const catalogue = Array.isArray(dashboardData.catalogue)
      ? dashboardData.catalogue
      : [];

    if (catalogue.length === 0) {
      setCatalogueItems([
        {
          id: Date.now(),
          name: "",
          location: "",
          imageUrl: null,
          file: null,
          preview: null,
          _id: null,
        },
      ]);
      setDeletedIds([]);
      return;
    }

    const mapped = catalogue.map((cat) => ({
      id: cat._id,
      _id: cat._id,
      name: cat.name || "",
      location: cat.location || "",
      imageUrl: cat.imageUrl || null,
      file: null,
      preview: null,
    }));

    setCatalogueItems(mapped);
    setDeletedIds([]);
  }, [dashboardData]);

  const handleInputChange = (id, field, value) => {
    if (!isEditing) return;

    setCatalogueItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const handleImageUpload = (id, event) => {
    if (!isEditing) return;
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setCatalogueItems((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                file,
                preview: e.target.result,
              }
            : item
        )
      );
    };
    reader.readAsDataURL(file);
  };

  const addCatalogueItem = () => {
    if (!isEditing) return;
    if (catalogueItems.length >= 5) {
      toast.warn("Maximum 5 catalogue items allowed.");
      return;
    }

    const newItem = {
      id: Date.now(),
      name: "",
      location: "",
      imageUrl: null,
      file: null,
      preview: null,
      _id: null,
    };
    setCatalogueItems((prev) => [...prev, newItem]);
  };

  const removeCatalogueItem = (id) => {
    if (!isEditing || catalogueItems.length <= 1) return;

    setCatalogueItems((prev) => {
      const toRemove = prev.find((i) => i.id === id);
      if (toRemove && toRemove._id) {
        setDeletedIds((old) =>
          old.includes(toRemove._id) ? old : [...old, toRemove._id]
        );
      }
      return prev.filter((item) => item.id !== id);
    });
  };

  const handleEdit = () => {
    if (!dashboardData) {
      toast.error("Dashboard not loaded yet.");
      return;
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!documentId) {
      toast.error("Dashboard not initialized yet (no documentId).");
      return;
    }

    try {
      setSaving(true);

      for (const delId of deletedIds) {
        try {
          await dispatch(deleteCatalogueItem(delId)).unwrap();
        } catch (err) {
          console.error("Error deleting catalogue item:", delId, err);
          toast.error(err || `Failed to delete catalogue item.`);
        }
      }

      for (const item of catalogueItems) {
        const hasBackendId = !!item._id;
        const hasName = item.name && item.name.trim() !== "";

        if (!hasBackendId && !hasName && !item.file) continue;

        if (!hasBackendId) {
          if (!hasName || !item.file) {
            toast.error("New items need Name and Image.");
            continue;
          }

          const formData = new FormData();
          formData.append("name", item.name);
          if (item.location) formData.append("location", item.location);
          formData.append("image", item.file);

          try {
            await dispatch(createCatalogueItem(formData)).unwrap();
          } catch (err) {
            console.error("Error creating catalogue item:", err);
            toast.error(err || "Failed to create catalogue item.");
          }
        } else {
          const formData = new FormData();
          formData.append("documentId", documentId);
          if (hasName) formData.append("name", item.name);
          if (item.location) formData.append("location", item.location);
          if (item.file) {
            formData.append("image", item.file);
          }

          try {
            await dispatch(
              updateCatalogueItem({ id: item._id, formData })
            ).unwrap();
          } catch (err) {
            console.error("Error updating catalogue item:", err);
            toast.error(err || "Failed to update catalogue item.");
          }
        }
      }

      toast.success("Catalogue saved successfully.");
      setIsEditing(false);
      setDeletedIds([]);
      await dispatch(fetchDashboard()).unwrap();
    } catch (err) {
      console.error("Error saving catalogue:", err);
      toast.error("Failed to save catalogue.");
    } finally {
      setSaving(false);
    }
  };

  const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL;

  const imgSrcFor = (item) => {
    if (item.preview) return item.preview;
    if (item.imageUrl)
      return item.imageUrl.startsWith("http")
        ? item.imageUrl
        : `${IMAGE_BASE_URL}${item.imageUrl}`;
    return null;
  };

  return (
    <div className="enhanced-dashboard">
      {/* Page Header */}
      <div className="page-header-banner catalogue-header">
        <div className="header-content-wrapper">
          <div className="header-icon-box">
            <Package size={28} />
          </div>
          <div className="header-text-content">
            <h1 className="header-main-heading">Catalogue</h1>
            <p className="header-sub-text">Showcase your catalogue and services (Max 5 items)</p>
          </div>
        </div>
        <div className={`status-indicator ${isEditing ? 'editing' : 'viewing'}`}>
          {isEditing ? (
            <>
              <AlertCircle size={16} />
              <span>Editing Mode</span>
            </>
          ) : (
            <>
              <CheckCircle size={16} />
              <span>View Mode</span>
            </>
          )}
        </div>
      </div>

      <div className="dashboard-content-area">
        {/* Items Counter */}
        <div className="items-counter-bar">
          <div className="counter-info">
            <Layers size={20} />
            <span>{catalogueItems.length} of 5 items used</span>
          </div>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: `${(catalogueItems.length / 5) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Catalogue Items */}
        <div className="catalogue-grid">
          {dashboardLoading && !dashboardData && (
            <div className="loading-placeholder">
              <div className="loader-spinner"></div>
              <p>Loading catalogue...</p>
            </div>
          )}

          {catalogueItems.map((item, index) => {
            const imgSrc = imgSrcFor(item);
            return (
              <div key={item.id} className="catalogue-card">
                <div className="catalogue-card-header">
                  <span className="catalogue-item-number">Item {index + 1}</span>
                  {isEditing && catalogueItems.length > 1 && (
                    <button
                      className="catalogue-remove-btn"
                      onClick={() => removeCatalogueItem(item.id)}
                      title="Remove item"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>

                {/* Image Upload Area */}
                <div className="catalogue-image-section">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(item.id, e)}
                    className="hidden-file-input"
                    id={`catalogue-image-${item.id}`}
                    disabled={!isEditing}
                  />
                  <label
                    htmlFor={`catalogue-image-${item.id}`}
                    className={`catalogue-image-upload ${!isEditing ? 'disabled' : ''} ${imgSrc ? 'has-image' : ''}`}
                  >
                    {imgSrc ? (
                      <>
                        <img
                          src={imgSrc}
                          alt="Catalogue item"
                          className="catalogue-preview-image"
                        />
                        {isEditing && (
                          <div className="image-overlay">
                            <Camera size={24} />
                            <span>Change Image</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="upload-placeholder">
                        <div className="upload-icon-circle">
                          <Image size={32} />
                        </div>
                        <span className="upload-label">
                          {isEditing ? "Click to Upload" : "No Image"}
                        </span>
                        {isEditing && (
                          <span className="upload-hint">JPG, PNG up to 5MB</span>
                        )}
                      </div>
                    )}
                  </label>
                </div>

                {/* Input Fields */}
                <div className="catalogue-details-section">
                  <div className="catalogue-input-group">
                    <label className="catalogue-input-label">
                      <User size={14} />
                      Name
                    </label>
                    <input
                      type="text"
                      className="catalogue-input"
                      placeholder="Enter name"
                      value={item.name}
                      onChange={(e) =>
                        handleInputChange(item.id, "name", e.target.value)
                      }
                      disabled={!isEditing}
                    />
                  </div>

                  <div className="catalogue-input-group">
                    <label className="catalogue-input-label">
                      <MapPin size={14} />
                      Location
                    </label>
                    <input
                      type="text"
                      className="catalogue-input"
                      placeholder="Enter location"
                      value={item.location}
                      onChange={(e) =>
                        handleInputChange(item.id, "location", e.target.value)
                      }
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          {/* Add New Item Card */}
          {isEditing && catalogueItems.length < 5 && (
            <div className="catalogue-card add-new-card" onClick={addCatalogueItem}>
              <div className="add-new-content">
                <div className="add-new-icon">
                  <Plus size={32} />
                </div>
                <span className="add-new-text">Add New Item</span>
                <span className="add-new-hint">{5 - catalogueItems.length} slots remaining</span>
              </div>
            </div>
          )}
        </div>

        {/* Empty State */}
        {catalogueItems.length === 0 && !dashboardLoading && (
          <div className="empty-state-card">
            <div className="empty-icon-wrapper">
              <FolderOpen size={48} />
            </div>
            <h3 className="empty-title">No Catalogue Items</h3>
            <p className="empty-description">
              Start adding your products or services to showcase them to customers.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons-row">
          {!isEditing ? (
            <button
              className="action-btn-enhanced edit-btn-enhanced"
              onClick={handleEdit}
              disabled={dashboardLoading || !dashboardData || saving || updating}
            >
              <Edit3 size={20} />
              Edit Catalogue
            </button>
          ) : (
            <>
              <button
                className="action-btn-enhanced cancel-btn"
                onClick={() => {
                  setIsEditing(false);
                  dispatch(fetchDashboard());
                }}
              >
                <X size={20} />
                Cancel
              </button>
              <button
                className="action-btn-enhanced save-btn"
                onClick={handleSave}
                disabled={saving || updating}
              >
                {saving || updating ? (
                  <>
                    <span className="btn-loader"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Save Changes
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CataloguePage;
